/* SPEC-563 fix loop 2 — the fetch core (auth header, silent refresh on 401, error
 * unwrapping), moved VERBATIM out of api.ts so it has no imports and plain Node
 * tests can drive it (`http.test.ts`). api.ts re-exports ApiError and uses requestRaw;
 * nothing else changed in the move. */
const BASE = '';

// SPEC-102: a structured API error so callers can tell "data temporarily unavailable /
// stale" (503 — backend not loaded, refreshing, or a feed is frozen) apart from a
// generic failure, and render a stale affordance instead of a raw "503: {...}" string.
export class ApiError extends Error {
  status: number;
  detail: string;
  unavailable: boolean;
  /** The parsed JSON response body, when there was one. SPEC-563: a 409 on the nav layout
   *  carries the current layout + version here, so the client can reconcile. */
  body?: unknown;
  constructor(status: number, detail: string, body?: unknown) {
    const unavailable = status === 503;
    super(
      unavailable
        ? 'Data temporarily unavailable — the backend may be refreshing or a data feed is stale. Please try again shortly.'
        : `${status}: ${detail}`
    );
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.unavailable = unavailable;
    this.body = body;
  }
}

let _refreshing: Promise<boolean> | null = null;

async function _tryRefresh(): Promise<boolean> {
  try {
    const res = await fetch('/api/auth/refresh', {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) return false;
    const data = await res.json();
    localStorage.setItem('suryaiq_token', data.access_token);
    if (data.user) localStorage.setItem('suryaiq_user', JSON.stringify(data.user));
    return true;
  } catch {
    return false;
  }
}

// Shared fetch core: auth header + silent-refresh-on-401 + error unwrapping.
// Returns the raw Response on success so BOTH JSON callers (request<T>) and blob
// callers (downloadReportCSV) get identical token-refresh behaviour (SPEC-142 §3(9)).
export async function requestRaw(path: string, options?: RequestInit): Promise<Response> {
  const token = localStorage.getItem('suryaiq_token');
  const headers: Record<string, string> = {};
  // SPEC-403: let the browser set the multipart boundary for FormData uploads;
  // only force JSON on non-FormData bodies.
  if (!(options?.body instanceof FormData)) headers['Content-Type'] = 'application/json';
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      headers,
      credentials: 'include',
      ...options,
    });
  } catch (err: any) {
    // Network error — fetch itself failed (server down, CORS block, abort, etc.)
    const kind = err?.name === 'AbortError' ? 'Request aborted' :
                 err?.name === 'TypeError' ? 'Network error' : (err?.name || 'Error');
    throw new Error(`${kind} on ${path}: ${err?.message || 'Failed to fetch'}`);
  }

  if (res.status === 401) {
    // Attempt silent refresh (deduplicated across concurrent requests)
    if (!_refreshing) {
      _refreshing = _tryRefresh().finally(() => { _refreshing = null; });
    }
    const refreshed = await _refreshing;
    if (refreshed) {
      // Retry the original request with the new token
      const newToken = localStorage.getItem('suryaiq_token');
      const retryHeaders: Record<string, string> = {};
      if (!(options?.body instanceof FormData)) retryHeaders['Content-Type'] = 'application/json';
      if (newToken) retryHeaders['Authorization'] = `Bearer ${newToken}`;
      let retry: Response;
      try {
        retry = await fetch(`${BASE}${path}`, {
          headers: retryHeaders,
          credentials: 'include',
          ...options,
        });
      } catch (err: any) {
        // SPEC-563 fix loop 2: the refresh SUCCEEDED, so the session is fine — a failed
        // network call is a network error, never a sign-out.
        const kind = err?.name === 'AbortError' ? 'Request aborted' :
                     err?.name === 'TypeError' ? 'Network error' : (err?.name || 'Error');
        throw new Error(`${kind} on ${path}: ${err?.message || 'Failed to fetch'}`);
      }
      // SPEC-563 fix loop 2 (Codex finding 1): the retry ANSWERED. Anything but another 401
      // is that answer — a 409 conflict, a 400 reason, a 500 — and is handled below like any
      // response. Only a second 401 means the new token is refused, which is a sign-out.
      // Before this, every non-2xx retry signed the user out and its body was lost.
      if (retry.status !== 401) res = retry;
    }
    if (res.status === 401) {
      // Refresh failed (or the refreshed token was refused) — clean logout via event (no
      // reload loop).
      localStorage.removeItem('suryaiq_token');
      localStorage.removeItem('suryaiq_user');
      window.dispatchEvent(new Event('suryaiq:logout'));
      throw new Error('Session expired — please log in again');
    }
  }
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    // Unwrap FastAPI's { detail: ... } envelope for a clean message.
    let detail = text;
    let body: unknown;
    try {
      const parsed = JSON.parse(text);
      body = parsed;
      if (parsed && typeof parsed.detail === 'string') detail = parsed.detail;
      else if (parsed && parsed.detail != null) detail = JSON.stringify(parsed.detail);
    } catch {
      // Body wasn't JSON — keep the raw text.
    }
    throw new ApiError(res.status, detail, body);
  }
  return res;
}
